import {
  useBoundProp,
  useCardSession,
  useCardStatus,
  useCardSessionControls,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';
import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu/dropdown-menu';
import { XStack } from '@/components/ui/layout/layout';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { cn } from '@/lib/utils';

import { InlineField, PillField } from '../../field';
import { FIELD_ERROR_BORDER_CLASS } from '../../field/textAppearance';
import {
  fieldErrorId,
  labelIdOf,
  useElementId,
  useFieldChecks,
} from '../../fieldIds';
import { Avatar } from '../../internals/Avatar';
import { useGhostPills } from '../../internals/objectContext';
import {
  HELD_CLASS,
  GHOST_PILL_CLASS,
  ICON_CLASS,
  ICON_SM_CLASS,
  PILL_CLASS,
  PILL_ERROR_CLASS,
  PILL_PLACEHOLDER_CLASS,
  QUIET_PICKER_CLASS,
  QUIET_TEXT_CLASS,
  sizingClass,
  TEXT_BOX_CLASS,
  TEXT_STYLE,
} from '../../tokens';
import { ICON_GLYPH } from '../Icon';

import {
  OptionContent,
  OptionMarks,
  type Option,
  type OptionValue,
} from './OptionContent';

import type { catalog } from '@littlebird/action-cards-view';

// Whether the option draws its own colour dot, icon or avatar; an empty
// avatar_url draws none
const hasOwnMark = (option: Option): boolean =>
  Boolean(option.color) || Boolean(option.icon) || Boolean(option.avatar_url);

// A single choice from a menu of options, each with an optional avatar, icon,
// colour and detail line
export const Select: ComponentFn<typeof catalog, 'Select'> = ({
  props,
  bindings,
}) => {
  const id = useElementId(props);
  const reducedMotion = useReducedMotion();
  const ghostPills = useGhostPills();
  const [value, setValue] = useBoundProp<OptionValue | null>(
    props.value,
    bindings?.value
  );
  const checks = useFieldChecks({
    path: bindings?.value,
    checks: props.checks,
    validateOn: props.validateOn,
    serverError: props.error,
  });
  const session = useCardSession();
  const status = useCardStatus();
  const controls = useCardSessionControls();
  const path = bindings?.value;
  const options = props.options ?? [];
  const selected = options.find((option) => option.value === value);
  const held = status === 'running';
  // A literal value has no state path to write to, so a pick would be lost
  const editable =
    path !== undefined &&
    props.editable !== false &&
    options.length > 0 &&
    !session.readOnly &&
    !held;
  const error = checks.error ?? props.error;
  const appearance = props.appearance ?? 'field';
  // A quiet picker is a pill at rest without its border: the object's footer
  const quiet = appearance === 'quiet';
  const pill = appearance === 'pill' || quiet;
  const pillClass = quiet ? QUIET_PICKER_CLASS : PILL_CLASS;
  // The element's icon stands in only while the picked option has no mark of its own
  const LeadingIcon =
    props.icon && !(selected && hasOwnMark(selected))
      ? ICON_GLYPH[props.icon]
      : undefined;
  const leading = LeadingIcon ? (
    <LeadingIcon
      className={cn(
        'shrink-0 text-foreground-muted',
        quiet ? ICON_SM_CLASS : ICON_CLASS
      )}
      aria-hidden="true"
    />
  ) : null;
  // A pill's label is visually hidden, so an empty pill names its field itself;
  // once locked, an empty pill with an icon shows the icon alone
  const emptyText = editable
    ? pill && props.label
      ? props.label
      : 'Choose…'
    : pill && LeadingIcon
      ? undefined
      : '—';
  const emptyValue = value === null || value === undefined;
  // A pill hides its label, so its tooltip names the field
  const tooltip = pill ? (error ?? props.label) : undefined;
  const placeholder =
    emptyValue && emptyText === undefined ? null : (
      <span className={pill ? PILL_PLACEHOLDER_CLASS : 'text-foreground-muted'}>
        {emptyValue ? emptyText : String(value)}
      </span>
    );

  // A pill holds its whole change, as a date pill does: one mark, the old value
  // struck, an arrow, then the new label in the pill's own text size. The old
  // value's marks stay out, or the pill would draw a second one
  const shown = pill ? (
    <>
      {selected ? <OptionMarks option={selected} avatarSize={16} /> : null}
      {leading}
      {props.previous ? (
        <>
          <span className="whitespace-nowrap text-foreground-muted line-through">
            {props.previous}
          </span>
          <span className="shrink-0 text-foreground-muted" aria-hidden="true">
            →
          </span>
        </>
      ) : null}
      {selected ? (
        <span className={cn('truncate', props.previous && 'font-semibold')}>
          {selected.label}
        </span>
      ) : (
        placeholder
      )}
    </>
  ) : (
    <>
      {leading}
      {selected ? <OptionContent option={selected} /> : placeholder}
    </>
  );

  const pressedInMenu = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // An optional empty pill is not drawn; focusing its path from the add line opens
  // its menu in place, and it leaves again if the menu closes with nothing picked
  const required =
    props.checks?.some((check) => check.type === 'required') ?? false;
  const hidesWhenEmpty =
    props.hideWhenEmpty === true &&
    appearance !== 'segmented' &&
    !required &&
    (value === null || value === undefined);
  const ghost = hidesWhenEmpty && ghostPills && editable;
  const summoned =
    hidesWhenEmpty && path !== undefined && session.focused === path;
  useEffect(() => {
    if (summoned && editable) setMenuOpen(true);
  }, [summoned, editable]);
  if (hidesWhenEmpty && !ghost && !menuOpen && !summoned) return null;

  const pick = (next: OptionValue | null) => {
    setValue(next);
    checks.afterChange();
    controls.commitNow();
  };

  const onFocus = () => {
    if (path) controls.focus(path);
  };
  const onBlur = () => {
    checks.onBlur();
    if (path) controls.blur(path);
  };

  let control: ReactNode;
  if (!editable) {
    control = (
      <span
        id={id}
        title={tooltip}
        aria-labelledby={props.label ? labelIdOf(id) : undefined}
        className={cn(
          quiet
            ? QUIET_TEXT_CLASS
            : pill
              ? pillClass
              : cn(
                  TEXT_BOX_CLASS,
                  'flex min-h-[var(--ac-field-lh)] items-center'
                ),
          held && HELD_CLASS
        )}
      >
        {shown}
      </span>
    );
  } else if (appearance === 'segmented') {
    control = (
      <SegmentedControl.Root
        value={selected ? String(selected.value) : undefined}
        onValueChange={(next) => {
          const option = options.find(
            (candidate) => String(candidate.value) === next
          );
          if (option) pick(option.value);
        }}
      >
        <SegmentedControl.List
          id={id}
          aria-labelledby={props.label ? labelIdOf(id) : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? fieldErrorId(id) : undefined}
          className={cn(
            'h-[calc(var(--ac-pill-h)+4*var(--ac-u))]',
            TEXT_STYLE.meta,
            error && FIELD_ERROR_BORDER_CLASS
          )}
          onFocus={onFocus}
          onBlur={onBlur}
        >
          {options.map((option) => {
            const Glyph = option.icon ? ICON_GLYPH[option.icon] : undefined;
            return (
              <SegmentedControl.Trigger
                key={String(option.value)}
                value={String(option.value)}
                className="gap-[calc(6*var(--ac-u))] px-[calc(10*var(--ac-u))]"
              >
                {option.color ? (
                  <span
                    aria-hidden="true"
                    className="size-[calc(8*var(--ac-u))] shrink-0 rounded-full"
                    style={{ backgroundColor: option.color }}
                  />
                ) : null}
                {Glyph ? (
                  <Glyph
                    className={cn(ICON_CLASS, 'shrink-0')}
                    aria-hidden="true"
                  />
                ) : null}
                <span className="min-w-0 truncate">{option.label}</span>
              </SegmentedControl.Trigger>
            );
          })}
        </SegmentedControl.List>
      </SegmentedControl.Root>
    );
  } else {
    control = (
      <DropdownMenuRoot
        open={menuOpen}
        onOpenChange={(next) => {
          setMenuOpen(next);
          pressedInMenu.current = false;
          if (!next && path) {
            checks.onBlur();
            controls.blur(path);
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            id={id}
            type="button"
            title={tooltip}
            aria-labelledby={
              appearance !== 'field' && props.label ? labelIdOf(id) : undefined
            }
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? fieldErrorId(id) : undefined}
            className={
              pill
                ? cn(
                    pillClass,
                    ghost && GHOST_PILL_CLASS,
                    error && PILL_ERROR_CLASS
                  )
                : cn(
                    TEXT_BOX_CLASS,
                    'flex min-h-[var(--ac-field-lh)] items-center justify-between gap-[var(--ac-gap-sm)] hover:bg-surface-hover focus-visible:ring-1 focus-visible:ring-border-strongest',
                    error && FIELD_ERROR_BORDER_CLASS
                  )
            }
            onFocus={onFocus}
            onBlur={onBlur}
          >
            {pill ? (
              shown
            ) : (
              <>
                <span className="flex min-w-0 items-center gap-[var(--ac-gap-sm)]">
                  {shown}
                </span>
                <Icons.chevronDown
                  className={cn(ICON_CLASS, 'shrink-0 text-foreground-muted')}
                  aria-hidden="true"
                />
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="max-h-72 w-72"
          onPointerDown={() => {
            pressedInMenu.current = true;
          }}
        >
          <DropdownMenuRadioGroup
            value={selected ? String(selected.value) : undefined}
            onValueChange={(key) => {
              const option = options.find(
                (candidate) => String(candidate.value) === key
              );
              if (option) pick(option.value);
            }}
          >
            {options.map((option) => (
              <DropdownMenuRadioItem
                key={String(option.value)}
                value={String(option.value)}
                className="pr-8"
                // The menu picks an item on a release that began elsewhere, such as
                // the press that opened it, so only a press inside the menu counts
                onPointerUp={(event) => {
                  if (!pressedInMenu.current) event.preventDefault();
                }}
              >
                <OptionContent option={option} inMenu />
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          {props.clearLabel && !emptyValue ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-foreground-subtle data-[highlighted]:text-foreground-subtle"
                onSelect={() => pick(null)}
                onPointerUp={(event) => {
                  if (!pressedInMenu.current) event.preventDefault();
                }}
              >
                {props.clearLabel}
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenuRoot>
    );
  }

  // Outside a pill: the old value struck, an arrow, then the current picker
  const PreviousIcon = props.previousIcon
    ? ICON_GLYPH[props.previousIcon]
    : undefined;
  const previous =
    props.previous && !pill ? (
      <XStack className="min-w-0 items-center gap-[calc(6*var(--ac-u))] text-foreground-muted">
        {props.previousColor ? (
          <span
            aria-hidden="true"
            className="size-[calc(8*var(--ac-u))] shrink-0 rounded-full"
            style={{ backgroundColor: props.previousColor }}
          />
        ) : null}
        {PreviousIcon ? (
          <PreviousIcon
            className={cn(ICON_CLASS, 'shrink-0')}
            aria-hidden="true"
          />
        ) : null}
        {props.previousAvatarUrl ? (
          <Avatar
            id={props.previous}
            name={props.previous}
            avatarUrl={props.previousAvatarUrl}
            shape="circle"
            size={20}
          />
        ) : null}
        <span className={cn('truncate line-through', TEXT_STYLE.body)}>
          {props.previous}
        </span>
        <span aria-hidden="true" className={TEXT_STYLE.body}>
          →
        </span>
      </XStack>
    ) : null;

  const row = (
    <motion.div
      layout
      className="flex min-w-0 items-center gap-[calc(6*var(--ac-u))]"
      transition={{ duration: reducedMotion ? 0 : 0.18 }}
    >
      {previous}
      {control}
    </motion.div>
  );

  const field =
    appearance === 'field' ? (
      <InlineField
        id={id}
        label={props.label}
        labelsControl={editable}
        error={error}
      >
        {row}
      </InlineField>
    ) : (
      <PillField id={id} error={error} className={sizingClass(props.sizing)}>
        {props.label ? (
          <span id={labelIdOf(id)} className="sr-only">
            {props.label}
          </span>
        ) : null}
        {row}
      </PillField>
    );

  return appearance === 'field' && props.sizing === 'fill' ? (
    <div className={sizingClass(props.sizing)}>{field}</div>
  ) : (
    field
  );
};
